import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/styles';

import { IMAGE_MIN_WIDTH_PX } from './constants';

/** Invisible grab zones. Only horizontal travel resizes; left-hand corners grow leftward. */
const HANDLES = [
	{ corner: 'nw', direction: -1, className: 'left-0 top-0 cursor-nwse-resize' },
	{ corner: 'ne', direction: 1, className: 'right-0 top-0 cursor-nesw-resize' },
	{ corner: 'sw', direction: -1, className: 'bottom-0 left-0 cursor-nesw-resize' },
	{ corner: 'se', direction: 1, className: 'bottom-0 right-0 cursor-nwse-resize' },
] as const;

type Size = { width: number; height: number };

/** `startWidth` stays fixed for the whole drag; `current` is what gets committed on pointer up. */
type DragState = {
	direction: number;
	startX: number;
	startWidth: number;
	aspectRatio: number;
	current: Size;
};

const ImageNodeViewComponent: React.FC<NodeViewProps> = ({
	node,
	selected,
	editor,
	updateAttributes,
}) => {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const imgRef = useRef<HTMLImageElement | null>(null);
	const dragRef = useRef<DragState | null>(null);
	const [draftSize, setDraftSize] = useState<Size | null>(null);

	const { src, alt, width, height } = node.attrs as {
		src: string;
		alt: string | null;
		width: number | null;
		height: number | null;
	};

	const handlePointerDown = useCallback(
		(direction: number) => (event: React.PointerEvent<HTMLSpanElement>) => {
			const img = imgRef.current;
			if (!img) return;

			event.preventDefault();
			event.stopPropagation();
			event.currentTarget.setPointerCapture(event.pointerId);

			dragRef.current = {
				direction,
				startX: event.clientX,
				startWidth: img.offsetWidth,
				aspectRatio: img.offsetHeight / img.offsetWidth || 1,
				current: { width: img.offsetWidth, height: img.offsetHeight },
			};
		},
		[]
	);

	const handlePointerMove = useCallback(
		(event: React.PointerEvent<HTMLSpanElement>) => {
			const drag = dragRef.current;
			if (!drag) return;

			const maxWidth = editor.view.dom.clientWidth;
			const delta = (event.clientX - drag.startX) * drag.direction;
			const width = Math.min(Math.max(drag.startWidth + delta, IMAGE_MIN_WIDTH_PX), maxWidth);
			const size = { width, height: width * drag.aspectRatio };

			dragRef.current = { ...drag, current: size };
			setDraftSize(size);
		},
		[editor]
	);

	/** Also handles pointer cancel, so an interrupted drag can't leave a stuck draft size. */
	const handlePointerEnd = useCallback(
		(event: React.PointerEvent<HTMLSpanElement>) => {
			const drag = dragRef.current;
			if (!drag) return;

			if (event.currentTarget.hasPointerCapture(event.pointerId)) {
				event.currentTarget.releasePointerCapture(event.pointerId);
			}
			dragRef.current = null;
			setDraftSize(null);

			const width = Math.round(drag.current.width);
			// A handle click that never moved must not write a size and trigger the call site's autosave.
			if (width === Math.round(drag.startWidth)) return;

			updateAttributes({ width, height: Math.round(drag.current.height) });
		},
		[updateAttributes]
	);

	/** TipTap previews the node view's outer box, and only a document listener runs after it. */
	useEffect(() => {
		const overrideDragImage = (event: DragEvent) => {
			const img = imgRef.current;
			const wrapper = wrapperRef.current;
			const target = event.target;
			if (!img || !wrapper || !(target instanceof Node)) return;
			// The drag source is the wrapper's parent, so check both directions.
			if (!wrapper.contains(target) && !target.contains(wrapper)) return;

			const rect = img.getBoundingClientRect();
			event.dataTransfer?.setDragImage(img, event.clientX - rect.left, event.clientY - rect.top);
		};

		document.addEventListener('dragstart', overrideDragImage);
		return () => document.removeEventListener('dragstart', overrideDragImage);
	}, []);

	const rendered = draftSize ?? { width, height };

	return (
		<NodeViewWrapper
			ref={wrapperRef}
			as="div"
			// Block, not inline-block: an inline wrapper adds a baseline gap and traps the img's `my-2`, so spacing drifted from the static HTML.
			className="relative block max-w-full"
			data-drag-handle
		>
			{/* Base look (size caps, radius, spacing) lives in `.tiptap img` so read-only HTML matches. */}
			{/* next/image can't be used here: the src is an arbitrary S3 host and the node view needs raw layout control. */}
			{/* eslint-disable-next-line @next/next/no-img-element */}
			<img
				ref={imgRef}
				src={src}
				alt={alt ?? ''}
				width={rendered.width ? Math.round(rendered.width) : undefined}
				height={rendered.height ? Math.round(rendered.height) : undefined}
				draggable={false}
				className={cn(selected && 'ring-2 ring-primary-blue')}
			/>
			{editor.isEditable &&
				selected &&
				HANDLES.map(({ corner, direction, className }) => (
					<span
						key={corner}
						role="presentation"
						className={cn('absolute h-4 w-4', className)}
						onPointerDown={handlePointerDown(direction)}
						onPointerMove={handlePointerMove}
						onPointerUp={handlePointerEnd}
						onPointerCancel={handlePointerEnd}
					/>
				))}
		</NodeViewWrapper>
	);
};

export const ImageNodeView = React.memo(ImageNodeViewComponent);

ImageNodeView.displayName = 'ImageNodeView';
